import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { format } from 'date-fns'
import {
  Package, Plus, Edit2, X, CheckCircle,
  Calendar, ToggleLeft, ToggleRight, Tag, Search,
  RefreshCw, ChevronDown, ChevronUp, Wrench
} from 'lucide-react'

// ─── Helpers ────────────────────────────────────────────────────────────────

function today() {
  return format(new Date(), 'yyyy-MM-dd')
}

function availColor(available, total) {
  if (total === 0) return 'grey'
  const pct = available / total
  if (pct <= 0) return 'red'
  if (pct < 0.25) return 'red'
  if (pct < 0.50) return 'yellow'
  return 'green'
}

function AvailBadge({ available, total }) {
  const color = availColor(available, total)
  const styles = {
    green:  { background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' },
    yellow: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' },
    red:    { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
    grey:   { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      ...styles[color]
    }}>
      {available} / {total}
    </span>
  )
}

const ADJUSTMENT_TYPES = ['damage', 'loss', 'maintenance', 'repair', 'returned_to_service']
const CATALOGUE_CATEGORIES = ['tents', 'furniture', 'lighting', 'flooring', 'delivery', 'labour', 'misc']

// ─── Modal: Add/Edit Category ────────────────────────────────────────────────

function CategoryModal({ category, onClose, onSave }) {
  const [form, setForm] = useState({
    name: category?.name || '',
    description: category?.description || '',
    total_quantity: category?.total_quantity || '',
    unit: category?.unit || 'unit',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSave() {
    if (!form.name.trim()) return setError('Name is required.')
    if (!form.total_quantity || form.total_quantity < 0) return setError('Quantity must be 0 or more.')
    setSaving(true)
    setError('')
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      total_quantity: parseInt(form.total_quantity, 10),
      unit: form.unit.trim() || 'unit',
    }
    let error
    if (category?.id) {
      ;({ error } = await supabase.from('inventory_categories').update(payload).eq('id', category.id))
    } else {
      ;({ error } = await supabase.from('inventory_categories').insert(payload))
    }
    setSaving(false)
    if (error) return setError(error.message)
    onSave()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>
            {category ? 'Edit Category' : 'Add Inventory Category'}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Category Name *</label>
            <input className="form-input" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. 20x20 Frame Tent" />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description" />
          </div>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Total Owned *</label>
              <input className="form-input" type="number" min="0" value={form.total_quantity}
                onChange={e => setForm(f => ({ ...f, total_quantity: e.target.value }))}
                placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Unit</label>
              <input className="form-input" value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                placeholder="unit" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : category ? 'Save Changes' : 'Add Category'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal: Add Adjustment ───────────────────────────────────────────────────

function AdjustmentModal({ categories, onClose, onSave }) {
  const [form, setForm] = useState({
    inventory_category_id: '',
    adjustment_type: 'damage',
    quantity_affected: '',
    start_date: today(),
    end_date: '',
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSave() {
    if (!form.inventory_category_id) return setError('Select a category.')
    if (!form.quantity_affected || form.quantity_affected < 1) return setError('Quantity must be at least 1.')
    if (!form.start_date) return setError('Start date is required.')
    setSaving(true)
    setError('')
    const { error } = await supabase.from('inventory_adjustments').insert({
      inventory_category_id: form.inventory_category_id,
      adjustment_type: form.adjustment_type,
      quantity_affected: parseInt(form.quantity_affected, 10),
      start_date: form.start_date,
      end_date: form.end_date || null,
      notes: form.notes.trim(),
      resolved: false,
    })
    setSaving(false)
    if (error) return setError(error.message)
    onSave()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>Log Inventory Adjustment</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Category *</label>
            <select className="form-select" value={form.inventory_category_id}
              onChange={e => setForm(f => ({ ...f, inventory_category_id: e.target.value }))}>
              <option value="">Select category…</option>
              {categories.filter(c => c.active).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Type *</label>
              <select className="form-select" value={form.adjustment_type}
                onChange={e => setForm(f => ({ ...f, adjustment_type: e.target.value }))}>
                {ADJUSTMENT_TYPES.map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Qty Affected *</label>
              <input className="form-input" type="number" min="1" value={form.quantity_affected}
                onChange={e => setForm(f => ({ ...f, quantity_affected: e.target.value }))} />
            </div>
          </div>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Start Date *</label>
              <input className="form-input" type="date" value={form.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Expected Return</label>
              <input className="form-input" type="date" value={form.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="What happened? Which unit(s)?" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Log Adjustment'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal: Add/Edit Catalogue Item ─────────────────────────────────────────

function CatalogueModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    name: item?.name || '',
    category: item?.category || 'tents',
    default_unit_price: item?.default_unit_price || '',
    unit: item?.unit || 'each',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleSave() {
    if (!form.name.trim()) return setError('Name is required.')
    if (form.default_unit_price === '' || form.default_unit_price < 0) return setError('Price must be 0 or more.')
    setSaving(true)
    setError('')
    const payload = {
      name: form.name.trim(),
      category: form.category,
      default_unit_price: parseFloat(form.default_unit_price) || 0,
      unit: form.unit.trim() || 'each',
    }
    let error
    if (item?.id) {
      ;({ error } = await supabase.from('catalogue_items').update(payload).eq('id', item.id))
    } else {
      ;({ error } = await supabase.from('catalogue_items').insert(payload))
    }
    setSaving(false)
    if (error) return setError(error.message)
    onSave()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>
            {item ? 'Edit Catalogue Item' : 'Add Catalogue Item'}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Item Name *</label>
            <input className="form-input" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Folding Chair Rental" />
          </div>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select className="form-select" value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATALOGUE_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Unit</label>
              <input className="form-input" value={form.unit}
                onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
                placeholder="each / hour / km…" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Default Unit Price ($) *</label>
            <input className="form-input" type="number" min="0" step="0.01" value={form.default_unit_price}
              onChange={e => setForm(f => ({ ...f, default_unit_price: e.target.value }))} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function InventoryPage() {
  const [activeTab, setActiveTab] = useState('categories')
  const [categories, setCategories] = useState([])
  const [adjustments, setAdjustments] = useState([])
  const [catalogueItems, setCatalogueItems] = useState([])
  const [availability, setAvailability] = useState([])
  const [availChecked, setAvailChecked] = useState(false)
  const [startDate, setStartDate] = useState(today())
  const [endDate, setEndDate] = useState(today())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [checkingAvail, setCheckingAvail] = useState(false)
  const [showResolved, setShowResolved] = useState(false)
  const [catSearch, setCatSearch] = useState('')

  // Modals
  const [catModal, setCatModal] = useState(null) // null | 'add' | {category obj}
  const [adjModal, setAdjModal] = useState(false)
  const [catItemModal, setCatItemModal] = useState(null) // null | 'add' | {item obj}

  const loadCategories = useCallback(async () => {
    const { data, error } = await supabase
      .from('inventory_categories')
      .select('*')
      .order('name')
    if (error) throw error
    setCategories(data || [])
  }, [])

  const loadAdjustments = useCallback(async () => {
    const { data, error } = await supabase
      .from('inventory_adjustments')
      .select('*, inventory_categories(name)')
      .order('created_at', { ascending: false })
    if (error) throw error
    setAdjustments(data || [])
  }, [])

  const loadCatalogueItems = useCallback(async () => {
    const { data, error } = await supabase
      .from('catalogue_items')
      .select('*')
      .order('category')
      .order('name')
    if (error) throw error
    setCatalogueItems(data || [])
  }, [])

  useEffect(() => {
    async function init() {
      setLoading(true)
      setError('')
      try {
        await Promise.all([loadCategories(), loadAdjustments(), loadCatalogueItems()])
      } catch (err) {
        console.error('Failed to load inventory data:', err)
        setError(err?.message || 'Failed to load inventory data.')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [loadCategories, loadAdjustments, loadCatalogueItems])

  async function checkAvailability() {
    setCheckingAvail(true)
    setError('')
    try {
      const { data, error } = await supabase.rpc('get_inventory_availability', {
        check_start: startDate,
        check_end: endDate,
      })
      if (error) throw error
      setAvailability(data || [])
      setAvailChecked(true)
    } catch (err) {
      console.error('Failed to check inventory availability:', err)
      setError(err?.message || 'Failed to check availability.')
      setAvailability([])
      setAvailChecked(false)
    } finally {
      setCheckingAvail(false)
    }
  }

  async function toggleCategoryActive(cat) {
    await supabase
      .from('inventory_categories')
      .update({ active: !cat.active })
      .eq('id', cat.id)
    loadCategories()
  }

  async function resolveAdjustment(id) {
    await supabase
      .from('inventory_adjustments')
      .update({ resolved: true })
      .eq('id', id)
    loadAdjustments()
  }

  async function toggleCatalogueActive(item) {
    await supabase
      .from('catalogue_items')
      .update({ active: !item.active })
      .eq('id', item.id)
    loadCatalogueItems()
  }

  const availMap = {}
  availability.forEach(a => { availMap[a.category_id] = a })

  const activeAdj = adjustments.filter(a => !a.resolved)
  const resolvedAdj = adjustments.filter(a => a.resolved)

  const filteredCats = categories.filter(c =>
    c.name.toLowerCase().includes(catSearch.toLowerCase())
  )

  const activeItems = catalogueItems.filter(i => i.active)
  const inactiveItems = catalogueItems.filter(i => !i.active)

  if (loading) return <div style={{ padding: 32, color: 'var(--color-text-muted)' }}>Loading inventory…</div>

  return (
    <div>
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Inventory</h1>
          <p className="page-subtitle">Manage equipment, track adjustments, and check date availability</p>
        </div>
        {activeTab === 'categories' && (
          <button className="btn btn-primary" onClick={() => setCatModal('add')}>
            <Plus size={15} /> Add Category
          </button>
        )}
        {activeTab === 'adjustments' && (
          <button className="btn btn-primary" onClick={() => setAdjModal(true)}>
            <Plus size={15} /> Log Adjustment
          </button>
        )}
        {activeTab === 'catalogue' && (
          <button className="btn btn-primary" onClick={() => setCatItemModal('add')}>
            <Plus size={15} /> Add Item
          </button>
        )}
      </div>

      {/* Stats Row */}
      <div className="stats-grid" style={{ marginBottom: 'var(--sp-6)' }}>
        <div className="stat-card">
          <div className="stat-label">Categories</div>
          <div className="stat-value">{categories.filter(c => c.active).length}</div>
          <div className="stat-sub">active in service</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Adjustments</div>
          <div className="stat-value" style={{ color: activeAdj.length > 0 ? 'var(--color-amber)' : undefined }}>
            {activeAdj.length}
          </div>
          <div className="stat-sub">damage / loss / maintenance</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Catalogue Items</div>
          <div className="stat-value">{activeItems.length}</div>
          <div className="stat-sub">active price items</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 'var(--sp-5)', borderBottom: '1px solid var(--color-border)', paddingBottom: 0 }}>
        {[
          { key: 'categories', label: 'Categories', icon: Package },
          { key: 'adjustments', label: 'Adjustments', icon: Wrench },
          { key: 'catalogue', label: 'Price Catalogue', icon: Tag },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', border: 'none', cursor: 'pointer',
              background: 'transparent',
              fontFamily: 'var(--font-body)', fontSize: 'var(--text-sm)', fontWeight: 500,
              color: activeTab === key ? 'var(--color-navy)' : 'var(--color-text-muted)',
              borderBottom: activeTab === key ? '2px solid var(--color-navy)' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.12s',
            }}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* ── CATEGORIES TAB ─────────────────────────────────────────────── */}
      {activeTab === 'categories' && (
        <div>
          {/* Availability Checker */}
          <div className="card" style={{ marginBottom: 'var(--sp-5)' }}>
            <div className="card-body" style={{ paddingTop: 16, paddingBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Calendar size={16} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                  Check Availability
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="date" className="form-input" value={startDate}
                    onChange={e => { setStartDate(e.target.value); setAvailChecked(false) }}
                    style={{ width: 150 }}
                  />
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>to</span>
                  <input
                    type="date" className="form-input" value={endDate}
                    onChange={e => { setEndDate(e.target.value); setAvailChecked(false) }}
                    style={{ width: 150 }}
                  />
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={checkAvailability}
                  disabled={checkingAvail}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <RefreshCw size={13} className={checkingAvail ? 'spin' : ''} />
                  {checkingAvail ? 'Checking…' : 'Check Availability'}
                </button>
                {availChecked && !checkingAvail && (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    Showing availability for {startDate === endDate ? startDate : `${startDate} – ${endDate}`}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Availability legend */}
          {availChecked && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 'var(--sp-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#065f46', display: 'inline-block' }} />
                {'>'}50% available
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#92400e', display: 'inline-block' }} />
                25–50% available
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#991b1b', display: 'inline-block' }} />
                {'<'}25% available
              </span>
            </div>
          )}

          {/* Search */}
          <div className="search-bar" style={{ marginBottom: 'var(--sp-3)', maxWidth: 280 }}>
            <Search size={14} className="search-icon" />
            <input
              placeholder="Search categories…"
              value={catSearch}
              onChange={e => setCatSearch(e.target.value)}
            />
          </div>

          {/* Categories Table */}
          <div className="card">
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Total Owned</th>
                    {availChecked && <th>Available ({startDate === endDate ? startDate : `${startDate}–${endDate}`})</th>}
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCats.length === 0 && (
                    <tr>
                      <td colSpan={availChecked ? 6 : 5} style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-muted)' }}>
                        No categories found.
                      </td>
                    </tr>
                  )}
                  {filteredCats.map(cat => {
                    const avail = availMap[cat.id]
                    return (
                      <tr key={cat.id} style={{ opacity: cat.active ? 1 : 0.55 }}>
                        <td style={{ fontWeight: 600 }}>{cat.name}</td>
                        <td style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>
                          {cat.description || '—'}
                        </td>
                        <td>
                          <span style={{ fontWeight: 600 }}>{cat.total_quantity}</span>
                          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)', marginLeft: 4 }}>{cat.unit}</span>
                        </td>
                        {availChecked && (
                          <td>
                            {avail
                              ? <AvailBadge available={avail.available_quantity} total={cat.total_quantity} />
                              : <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>—</span>
                            }
                          </td>
                        )}
                        <td>
                          <span className={`badge ${cat.active ? 'badge-confirmed' : 'badge-cancelled'}`}>
                            {cat.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setCatModal(cat)} title="Edit">
                              <Edit2 size={13} />
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => toggleCategoryActive(cat)}
                              title={cat.active ? 'Deactivate' : 'Activate'}
                              style={{ color: cat.active ? 'var(--color-red)' : 'var(--color-green)' }}
                            >
                              {cat.active ? <ToggleLeft size={14} /> : <ToggleRight size={14} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── ADJUSTMENTS TAB ────────────────────────────────────────────── */}
      {activeTab === 'adjustments' && (
        <div>
          {/* Active Adjustments */}
          <div style={{ marginBottom: 'var(--sp-3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>
              Active Adjustments
              <span style={{ marginLeft: 8, fontSize: 'var(--text-sm)', fontWeight: 400, color: 'var(--color-text-muted)' }}>
                ({activeAdj.length})
              </span>
            </h3>
          </div>

          <div className="card" style={{ marginBottom: 'var(--sp-6)' }}>
            {activeAdj.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={36} style={{ opacity: 0.3, color: 'var(--color-green)' }} />
                <p>No active adjustments — all inventory is in service.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Qty</th>
                      <th>Start Date</th>
                      <th>Expected Return</th>
                      <th>Notes</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeAdj.map(adj => (
                      <tr key={adj.id}>
                        <td style={{ fontWeight: 600 }}>{adj.inventory_categories?.name || '—'}</td>
                        <td>
                          <span className={`badge ${adj.adjustment_type === 'damage' ? 'badge-declined' : adj.adjustment_type === 'loss' ? 'badge-overdue' : 'badge-quoted'}`}>
                            {adj.adjustment_type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{adj.quantity_affected}</td>
                        <td style={{ fontSize: 'var(--text-sm)' }}>{adj.start_date}</td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                          {adj.end_date || '—'}
                        </td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', maxWidth: 200 }}>
                          {adj.notes || '—'}
                        </td>
                        <td>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => resolveAdjustment(adj.id)}
                            title="Mark as returned to service"
                          >
                            <CheckCircle size={13} /> Resolve
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Resolved Adjustments (collapsible) */}
          <button
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)',
              marginBottom: 'var(--sp-3)', padding: 0,
            }}
            onClick={() => setShowResolved(v => !v)}
          >
            {showResolved ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Resolved Adjustments ({resolvedAdj.length})
          </button>

          {showResolved && (
            <div className="card">
              {resolvedAdj.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                  No resolved adjustments yet.
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Type</th>
                        <th>Qty</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resolvedAdj.map(adj => (
                        <tr key={adj.id} style={{ opacity: 0.6 }}>
                          <td>{adj.inventory_categories?.name || '—'}</td>
                          <td>
                            <span className="badge badge-cancelled">{adj.adjustment_type.replace(/_/g, ' ')}</span>
                          </td>
                          <td>{adj.quantity_affected}</td>
                          <td style={{ fontSize: 'var(--text-sm)' }}>{adj.start_date}</td>
                          <td style={{ fontSize: 'var(--text-sm)' }}>{adj.end_date || '—'}</td>
                          <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{adj.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── CATALOGUE TAB ──────────────────────────────────────────────── */}
      {activeTab === 'catalogue' && (
        <div>
          {CATALOGUE_CATEGORIES.map(cat => {
            const items = catalogueItems.filter(i => i.category === cat)
            if (items.length === 0) return null
            return (
              <div key={cat} style={{ marginBottom: 'var(--sp-6)' }}>
                <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--sp-3)' }}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </h3>
                <div className="card">
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Default Price</th>
                          <th>Unit</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => (
                          <tr key={item.id} style={{ opacity: item.active ? 1 : 0.5 }}>
                            <td style={{ fontWeight: 500 }}>{item.name}</td>
                            <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>
                              ${parseFloat(item.default_unit_price).toFixed(2)}
                            </td>
                            <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                              {item.unit}
                            </td>
                            <td>
                              <span className={`badge ${item.active ? 'badge-confirmed' : 'badge-cancelled'}`}>
                                {item.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setCatItemModal(item)}>
                                  <Edit2 size={13} />
                                </button>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => toggleCatalogueActive(item)}
                                  style={{ color: item.active ? 'var(--color-red)' : 'var(--color-green)' }}
                                >
                                  {item.active ? <ToggleLeft size={14} /> : <ToggleRight size={14} />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          })}
          {catalogueItems.length === 0 && (
            <div className="empty-state">
              <Tag size={36} style={{ opacity: 0.3 }} />
              <p>No catalogue items yet. Add items to use in quotes.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      {catModal && (
        <CategoryModal
          category={catModal === 'add' ? null : catModal}
          onClose={() => setCatModal(null)}
          onSave={() => { setCatModal(null); loadCategories(); setAvailChecked(false) }}
        />
      )}

      {adjModal && (
        <AdjustmentModal
          categories={categories}
          onClose={() => setAdjModal(false)}
          onSave={() => { setAdjModal(false); loadAdjustments() }}
        />
      )}

      {catItemModal && (
        <CatalogueModal
          item={catItemModal === 'add' ? null : catItemModal}
          onClose={() => setCatItemModal(null)}
          onSave={() => { setCatItemModal(null); loadCatalogueItems() }}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  )
}
